import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { api } from "@/lib/api";

const inputCls = "w-full bg-white border border-navy/20 px-4 py-3 text-navy placeholder:text-navy/40 focus:outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all required fields."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const contacts = [
    { icon: Phone, label: "Phone", value: "+1-778-987-7884", href: "tel:+17789877884" },
    { icon: MessageCircle, label: "WhatsApp", value: "778-987-7884  /  647-685-2707", href: "https://wa.me/17789877884" },
    { icon: Mail, label: "Email", value: "pmpsar@gmail.com", href: "mailto:pmpsar@gmail.com" },
    { icon: MapPin, label: "Location", value: "British Columbia, Canada", href: null },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="/ GET IN TOUCH" title={["Let's", "talk material."]}
        subtitle="Reach out for pricing, pickups or a long-term recurring-contract purchase program." />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="border-t border-navy/10">
            {contacts.map((c) => (
              <div key={c.label} className="flex items-start gap-4 py-6 border-b border-navy/10" data-testid={`contact-${c.label}`}>
                <c.icon className="text-emerald shrink-0 mt-1" size={22} strokeWidth={1.5} />
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-navy/50">{c.label}</div>
                  {c.href
                    ? <a href={c.href} target="_blank" rel="noopener noreferrer" className="text-navy text-lg hover:text-emerald transition-colors">{c.value}</a>
                    : <span className="text-navy text-lg">{c.value}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <FadeUp>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="contact-name" />
                <input className={inputCls} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="contact-email" />
              </div>
              <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
              <textarea className={`${inputCls} min-h-[160px] resize-none`} placeholder="Your message *" value={form.message} onChange={set("message")} data-testid="contact-message" />
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="w-full bg-navy text-white font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-emerald hover:text-navy transition-colors disabled:opacity-50">
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Contact;
